import { ChartColor } from '../../helper/charthelper';
import { Filter } from '../search/types';

export const reportingFeatureKey = 'reporting';
export type LabelWithValue = {
  label: string;
  value: number;
};
export type GroupedLabelWithValue = {
  groupLabel: string;
  groupColor: ChartColor;
  values: LabelWithValue[];
};
export type LabelByDayAndHoursRange = {
  day: string;
  dayLabel: string;
  hourRange: string;
  value: number;
};
export type InputReporting = {
  parameters: string[];
  query: Map<string, string>;
  filters?: Filter[];
};
export interface MatrixDataPointExtended {
  x: number;
  y: number;
  v: number;
}
export type Period = {
  value: string;
  label: string;
  startDate: Date;
  endDate: Date;
  group: string;
};

/**
 * One row of the sales performance comparison dashboard.
 * Matches `Comparanoo.Core.Models.Reporting.SalesPerformanceReportItem` on the API.
 *
 * Special bucket conventions:
 * - `salesPersonId === null` → "Non attribués" (leads/contracts without a salesperson).
 * - `salesPersonId === -1`   → "Anciens salariés" (all inactive users aggregated into one row).
 * - `salesPersonId > 0`      → an active salesperson.
 *
 */
export type SalesPerformanceReportItem = {
  salesPersonId: number | null;
  salesPersonName: string;
  isActive: boolean;
  leadsCount: number;
  newContractsCount: number;
  distinctContactsCount: number;
  conversionRate: number;
  multiSaleRatio: number;
  pinned: boolean;
  isBoldRowStyle: boolean;
  leadsFilter: Filter;
  contractsFilter: Filter;
  contactsFilter: Filter;
};

/**
 * One row of the calls dashboard (back `CallsPerformanceReportItemModel`): the calls of a salesperson
 * in one office over the selected period. `salesPersonId` = -1 merges the office's former employees,
 * `null` its calls with no salesperson; both are pinned, above the bold pinned "Total" row.
 * Every counter carries a drill-down filter on exactly the calls it counts.
 */
export type CallsPerformanceReportItem = {
  salesPersonId: number | null;
  salesPersonName: string;
  salesTeamId: number | null;
  salesTeamName: string;
  callsCount: number;
  /** Distinct client numbers. */
  clientsCount: number;
  inboundCallsCount: number;
  servedInboundCallsCount: number;
  unservedInboundCallsCount: number;
  outboundCallsCount: number;
  /** Outbound calls picked up, the client's voicemail included. */
  reachedOutboundCallsCount: number;
  /** Served inbound / inbound calls, in percent (server-side). */
  responsivenessRate: number;
  /** Reached outbound / outbound calls, in percent (server-side). */
  reachabilityRate: number;
  pinned: boolean;
  isBoldRowStyle: boolean;
  callsFilter: Filter;
  /** The row's calls carrying a client number: more rows than `clientsCount` when a client was called twice. */
  clientsFilter: Filter;
  inboundCallsFilter: Filter;
  unservedInboundCallsFilter: Filter;
  outboundCallsFilter: Filter;
};

/**
 * Current selection of the calls dashboard (back `CallsPerformanceQueryModel`), sent as is to the
 * rows and to every dropdown so both cover the same calls.
 */
export type CallsPerformanceQuery = {
  /** First local day of the period (yyyy-MM-dd). */
  startDate: string;
  /** Last local day of the period, included (yyyy-MM-dd). */
  endDate: string;
  /** Direction facet ids (1 = Inbound, 2 = Outbound); empty = all. */
  directions: number[];
  /** null = calls with no office; empty = all. */
  salesTeamIds: (number | null)[];
  /** null = calls with no salesperson; empty = all. */
  salesPersonIds: (number | null)[];
};

export type CallsPerformanceFacetDimension =
  | 'Direction'
  | 'SalesTeam'
  | 'SalesPerson';

/**
 * One option of a calls dashboard dropdown (back `FacetItemModel`), with the number of calls it
 * covers given the other active filters. A null id is the bucket of calls with no value on the
 * dimension; for `Direction` the id is the `CallDirection` value (1 = Inbound, 2 = Outbound).
 */
export type CallsPerformanceFacetItem = {
  id: number | null;
  name: string;
  count: number;
};

/** Dropdown-binding shape: the null id has been replaced by `UNASSIGNED_FACET_ID`. */
export type CallsPerformanceFacetItemWithFlag = Omit<
  CallsPerformanceFacetItem,
  'id'
> & {
  id: number;
  disabled?: boolean;
};

/**
 * Request of one calls dashboard dropdown (back `CallsPerformanceFacetQueryModel`). Always the
 * whole selection: the back leaves out the requested dimension's own one.
 */
export type CallsPerformanceFacetQuery = CallsPerformanceQuery & {
  dimension: CallsPerformanceFacetDimension;
};

export type SalesPerformanceQuery = {
  filter: string;
};

/**
 * One row of the marketing performance comparison dashboard.
 */
export type MarketingPerformanceReportItem = {
  sourceId: number | null;
  sourceName: string;
  mediumId: number | null;
  mediumName: string;
  campaignId: number | null;
  campaignName: string;
  leadsCount: number;
  newContractsCount: number;
  distinctContactsCount: number;
  conversionRate: number;
  crossSellRatio: number;
  pinned: boolean;
  isBoldRowStyle: boolean;
  leadsFilter: Filter;
  contractsFilter: Filter[];
  contactsFilter: Filter[];
};

export type MarketingPerformanceQuery = {
  filter: string;
};

export type SalesPerformanceFacetDimension =
  | 'Source'
  | 'Medium'
  | 'Campaign'
  | 'ProductCategory'
  | 'SalesTeam'
  | 'SalesPerson';

// The "unassigned" bucket (a lead with no value on this dimension) is carried
// as a null id — the honest domain value. Consumers that can't bind null (e.g.
// ng-select) substitute their own UI sentinel; that concern stays out of here.
export type SalesPerformanceFacetItem = {
  id: number | null;
  name: string;
  count: number;
};

// Post-adaptation shape used for dropdown binding: the null id has been
// replaced by a concrete sentinel, so id is always a number here.
export type SalesPerformanceFacetItemWithFlag = Omit<
  SalesPerformanceFacetItem,
  'id'
> & {
  id: number;
  disabled?: boolean;
};

export type SalesPerformanceFacetQuery = {
  dimension: SalesPerformanceFacetDimension;
  filter: string;
};

// ng-select can't bind a null id (mapSelectedItems filters via isDefined), so
// the API's null "unassigned" bucket is shown under this sentinel and converted
// back to null when building the Gridify filter. -2 stays internal to this
// component; the API only ever speaks null.
export const UNASSIGNED_FACET_ID = -2;
